import { absenceDayPartError, absenceDayPartLabel, type AbsenceDayPart } from '../../absence/public';
import type { BulkWorkflow } from './bulkOperations';
import { formatPersonDisplayName } from '../../../shared/formatPersonDisplayName';

const templateHref = '/templates/bulk-upload-templates.xlsx';
const dateValidation = (label: string) => (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${label}은 YYYY-MM-DD 형식이어야 합니다.`;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value ? undefined : `${label}에 실제 날짜를 입력해 주세요.`;
};
const emailValidation = (value: string) => value.length <= 320 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? undefined : '개인 이메일 형식을 확인해 주세요.';
const phoneValidation = (value: string) => /^[0-9+(). -]{4,50}$/.test(value) ? undefined : '휴대전화에는 숫자와 + ( ) . - 공백만 사용할 수 있습니다.';
const systemIDValidation = (value: string) => /^[a-z0-9][a-z0-9._-]{0,20}$/.test(value) ? undefined : '아이디는 영문 소문자 또는 숫자로 시작하고 21자 이내여야 합니다.';
const maxLengthValidation = (label: string, max: number) => (value: string) => value.length <= max ? undefined : `${label}은 ${max.toLocaleString()}자 이하여야 합니다.`;
const positiveQuantityValidation = (value: string) => Number.isFinite(Number(value)) && Number(value) > 0 ? undefined : '수량은 0보다 큰 숫자여야 합니다.';
const nonZeroQuantityValidation = (value: string) => Number.isFinite(Number(value)) && Number(value) !== 0 ? undefined : '수량은 0이 아닌 숫자여야 합니다.';
const timeOffEndDateValidation = (value: string, row: Record<string, string>) => dateValidation('종료일')(value) ?? (row.startDate && value < row.startDate ? '종료일은 시작일과 같거나 이후여야 합니다.' : undefined);

const baseWorkflows: BulkWorkflow[] = [
  {
    operationType: 'EMPLOYEE_HIRE',
    label: 'Employee 대량 등록',
    description: 'Person을 생성하고 Employee 입사 초안을 기존 결재 흐름으로 제출합니다.',
    defaultsNotice: '근무 형태는 전일제(FULL_TIME) 또는 시간제(PART_TIME)로 입력하세요. 기존 양식에 근무 형태 열이 없거나 셀이 비어 있으면 전일제(FULL_TIME)로 적용합니다.',
    resourceLabel: 'Person + Employee 입사 승인 요청',
    templateHref,
    validateRow: (row) => Boolean(row.legalEnglishFamilyName) !== Boolean(row.legalEnglishGivenName)
      ? ['영문 이름을 입력할 때는 성과 이름을 함께 입력해 주세요.'] : [],
    columns: [
      { key: 'legalFamilyName', label: '법적 이름 · 성', required: true, example: '홍', validate: maxLengthValidation('법적 이름 · 성', 200) },
      { key: 'legalGivenName', label: '법적 이름 · 이름', required: true, example: '길동', validate: maxLengthValidation('법적 이름 · 이름', 200) },
      { key: 'legalEnglishFamilyName', label: '법적 이름(영문) · 성', example: 'Hong', validate: maxLengthValidation('법적 이름(영문) · 성', 200) },
      { key: 'legalEnglishGivenName', label: '법적 이름(영문) · 이름', example: 'Gildong', validate: maxLengthValidation('법적 이름(영문) · 이름', 200) },
      { key: 'nicknameKorean', label: '사내 닉네임(한글)', example: '길동', validate: maxLengthValidation('사내 닉네임(한글)', 200) },
      { key: 'nicknameEnglish', label: '사내 닉네임(영문)', example: 'Gil', validate: maxLengthValidation('사내 닉네임(영문)', 200) },
      { key: 'systemId', label: '아이디 (이메일 ID)', example: 'gildong.hong', validate: systemIDValidation },
      { key: 'homeEmail', label: '개인 이메일', example: 'gildong@example.com', validate: emailValidation },
      { key: 'homePhone', label: '휴대전화', example: '010-1234-5678', validate: phoneValidation },
      { key: 'companyId', label: '회사', required: true, example: '회사 이름' },
      { key: 'organizationId', label: '조직', required: true, example: '조직 이름' },
      { key: 'locationId', label: '근무지', required: true, example: '근무지 이름' },
      { key: 'jobProfileId', label: '직무', required: true, example: '직무 이름' },
      { key: 'businessTitle', label: '직책', example: 'Software Engineer' },
      { key: 'timeType', label: '근무 형태', example: '전일제', parse: (value) => (({ 전일제: 'FULL_TIME', 시간제: 'PART_TIME' } as Record<string, string>)[value] ?? (value || 'FULL_TIME')), validate: (value) => ['FULL_TIME', 'PART_TIME'].includes(value) ? undefined : '근무 형태는 전일제(FULL_TIME) 또는 시간제(PART_TIME)로 입력해 주세요.' },
      { key: 'hireDate', label: '입사일', required: true, example: '2026-09-01', validate: dateValidation('입사일') },
    ],
    previewColumns: [
      { label: '구성원', value: (row) => formatPersonDisplayName(row.nicknameKorean, `${row.legalFamilyName ?? ''}${row.legalGivenName ?? ''}`) },
      { label: '영문 이름', value: (row) => [row.legalEnglishGivenName, row.legalEnglishFamilyName].filter(Boolean).join(' ') || '미입력' },
      { label: '시스템 ID', value: (row) => row.systemId || '미입력' },
      { label: '회사·조직', value: (row) => `${row.companyId} · ${row.organizationId}` },
      { label: '직무·근무지', value: (row) => `${row.jobProfileId} · ${row.locationId}` },
      { label: '입사일', value: (row) => row.hireDate },
      { label: '근무 형태', value: (row) => (({ FULL_TIME: '전일제', PART_TIME: '시간제', 전일제: '전일제', 시간제: '시간제' } as Record<string, string>)[row.timeType] ?? (row.timeType || '전일제')) },
    ],
  },
  {
    operationType: 'EMPLOYEE_REHIRE',
    label: 'Employee 재입사',
    description: '기존 Person을 유지한 채 새 Employee 고용관계와 입사 승인 요청을 만듭니다.',
    resourceLabel: '기존 Person + 새 Employee 입사 승인 요청',
    templateHref,
    columns: [
      { key: 'personId', label: '재입사자', required: true, example: '재입사자 이름' },
      { key: 'companyId', label: '회사', required: true, example: '회사 이름' },
      { key: 'organizationId', label: '조직', required: true, example: '조직 이름' },
      { key: 'locationId', label: '근무지', required: true, example: '근무지 이름' },
      { key: 'jobProfileId', label: '직무', required: true, example: '직무 이름' },
      { key: 'businessTitle', label: '직책', example: 'Software Engineer' },
      { key: 'hireDate', label: '재입사일', required: true, example: '2026-09-01', validate: dateValidation('재입사일') },
    ],
    previewColumns: [
      { label: '재입사자', value: (row) => row.personId },
      { label: '회사·조직', value: (row) => `${row.companyId} · ${row.organizationId}` },
      { label: '직무·근무지', value: (row) => `${row.jobProfileId} · ${row.locationId}` },
      { label: '재입사일', value: (row) => row.hireDate },
    ],
  },
  {
    operationType: 'STAFFING_CHANGE',
    label: '인사 발령',
    description: '기존 구성원의 조직·직무 변경 초안을 결재 흐름으로 제출합니다.',
    resourceLabel: 'Job Change 승인 요청',
    templateHref,
    columns: [
      { key: 'workerId', label: '구성원', required: true, example: '구성원 이름 또는 사번' },
      { key: 'effectiveDate', label: '시행일', required: true, example: '2026-09-01', validate: dateValidation('시행일') },
      { key: 'organizationId', label: '이동 조직', example: '조직 이름' },
      { key: 'locationId', label: '이동 근무지', example: '근무지 이름' },
      { key: 'jobProfileId', label: '이동 직무', example: '직무 이름' },
      { key: 'businessTitle', label: '직책', example: 'Lead Engineer' },
      { key: 'reason', label: '사유', example: '정기 발령' },
    ],
    previewColumns: [
      { label: '구성원', value: (row) => row.workerId },
      { label: '변경 조직', value: (row) => row.organizationId },
      { label: '직무·직책', value: (row) => `${row.jobProfileId ?? ''} ${row.businessTitle ?? ''}`.trim() },
      { label: '시행일', value: (row) => row.effectiveDate },
    ],
  },
  {
    operationType: 'WORKER_LIFECYCLE_CORRECTION',
    label: '생명주기 정정',
    description: '완료된 입사·휴직·복직·종료 이벤트의 시행일 정정 초안을 만듭니다.',
    resourceLabel: 'Worker lifecycle 정정 승인 요청',
    templateHref,
    columns: [
      { key: 'eventId', label: '인사 기록', required: true, example: '구성원 이름 또는 인사 기록 이름' },
      { key: 'effectiveDate', label: '정정 시행일', required: true, example: '2026-09-01', validate: dateValidation('정정 시행일') },
      { key: 'reason', label: '정정 사유', required: true, example: '입사일 정정', validate: maxLengthValidation('정정 사유', 4000) },
    ],
    previewColumns: [
      { label: '인사 기록', value: (row) => row.eventId },
      { label: '정정 시행일', value: (row) => row.effectiveDate },
      { label: '사유', value: (row) => row.reason },
    ],
  },
  {
    operationType: 'TIME_OFF_CORRECTION',
    label: '휴가 정정',
    description: '기존 휴가 Entry를 삭제하지 않고 정정 버전을 만듭니다.',
    resourceLabel: 'Time Off Entry 정정 버전',
    templateHref,
    validateRow: (row) => {
      const partError = absenceDayPartError({ startDate: row.startDate, endDate: row.endDate, quantity: row.quantity, unitOfTime: row.unitOfTime, dayPart: row.dayPart as AbsenceDayPart });
      if (partError) return [partError];
      if (row.dayPart === 'FULL_DAY' && row.unitOfTime !== 'DAYS') return ['FULL_DAY는 DAYS 단위에만 사용할 수 있습니다.'];
      return [];
    },
    columns: [
      { key: 'workerId', label: '구성원', example: '구성원 이름 또는 사번' },
      { key: 'entryId', label: '휴가 기록', required: true, example: '휴가 기록 이름과 날짜' },
      { key: 'timeOffTypeId', label: '휴가 유형', required: true, example: '휴가 유형 이름' },
      { key: 'startDate', label: '시작일', required: true, example: '2026-09-01', validate: dateValidation('시작일') },
      { key: 'endDate', label: '종료일', required: true, example: '2026-09-01', validate: timeOffEndDateValidation },
      { key: 'quantity', label: '수량', required: true, example: '1', validate: positiveQuantityValidation },
      { key: 'dayPart', label: '사용 시간대 (0.5·0.25일 필수)', example: '', validate: (value) => ['', 'FULL_DAY', 'AM', 'PM', 'AM_QUARTER', 'PM_QUARTER'].includes(value) ? undefined : '반차는 AM·PM, 반반차는 AM_QUARTER·PM_QUARTER로 입력해 주세요.' },
      { key: 'unitOfTime', label: '단위', required: true, example: 'DAYS', validate: (value) => ['DAYS', 'HOURS'].includes(value) ? undefined : '단위는 DAYS 또는 HOURS여야 합니다.' },
      { key: 'reason', label: '정정 사유', required: true, example: '사용일 정정', validate: maxLengthValidation('정정 사유', 4000) },
    ],
    previewColumns: [
      { label: '휴가 기록', value: (row) => row.entryId },
      { label: '기간', value: (row) => `${row.startDate} ~ ${row.endDate}` },
      { label: '수량', value: (row) => `${row.quantity} ${row.unitOfTime}` },
      { label: '사용 시간대', value: (row) => absenceDayPartLabel(row.dayPart) || '해당 없음' },
      { label: '사유', value: (row) => row.reason },
    ],
  },
  {
    operationType: 'TIME_OFF_GRANT',
    label: '휴가 일괄 지급',
    description: '구성원의 기존 휴가 entitlement에 수동 지급 거래를 추가합니다.',
    resourceLabel: '휴가 지급 원장 거래',
    templateHref,
    columns: [
      { key: 'workerId', label: '구성원', required: true, example: '구성원 이름 또는 사번' },
      { key: 'entitlementId', label: '휴가 잔액', required: true, example: '휴가 잔액 이름' },
      { key: 'quantity', label: '지급 수량', required: true, example: '1', validate: positiveQuantityValidation },
      { key: 'effectiveOn', label: '적용일', required: true, example: '2026-09-01', validate: dateValidation('적용일') },
      { key: 'reason', label: '지급 사유', required: true, example: '창립기념 특별휴가', validate: maxLengthValidation('지급 사유', 4000) },
    ],
    previewColumns: [
      { label: '구성원', value: (row) => row.workerId },
      { label: '휴가 잔액', value: (row) => row.entitlementId },
      { label: '지급', value: (row) => `${row.quantity} · ${row.effectiveOn}` },
      { label: '사유', value: (row) => row.reason },
    ],
  },
  {
    operationType: 'TIME_OFF_ADJUSTMENT',
    label: '휴가 일괄 조정',
    description: '구성원의 기존 휴가 entitlement에 증감 조정 거래를 추가합니다.',
    resourceLabel: '휴가 조정 원장 거래',
    templateHref,
    columns: [
      { key: 'workerId', label: '구성원', required: true, example: '구성원 이름 또는 사번' },
      { key: 'entitlementId', label: '휴가 잔액', required: true, example: '휴가 잔액 이름' },
      { key: 'quantity', label: '조정 수량', required: true, example: '-0.5', validate: nonZeroQuantityValidation },
      { key: 'effectiveOn', label: '적용일', required: true, example: '2026-09-01', validate: dateValidation('적용일') },
      { key: 'reason', label: '조정 사유', required: true, example: '이관 잔액 정정', validate: maxLengthValidation('조정 사유', 4000) },
    ],
    previewColumns: [
      { label: '구성원', value: (row) => row.workerId },
      { label: '휴가 잔액', value: (row) => row.entitlementId },
      { label: '조정', value: (row) => `${row.quantity} · ${row.effectiveOn}` },
      { label: '사유', value: (row) => row.reason },
    ],
  },
];

export const employeeImportWorkflow: BulkWorkflow = {
 ...baseWorkflows[0],
 operationType: 'EMPLOYEE_IMPORT', label: '기존 재직자 이관',
 description: '사번과 입사일을 보존해 기존 재직자를 등록합니다. 현재 소속은 이관 기준일부터 기록하고 신규 입사 환영 알림은 발송하지 않습니다. 재입사·복수 고용 이력은 이 양식에서 가져올 수 없습니다.',
 resourceLabel: '기존 재직자 이관 승인 요청',
 validateRow: (row) => [...(baseWorkflows[0].validateRow?.(row) ?? []), ...(!row.legalName && (!row.legalFamilyName || !row.legalGivenName) ? ['법적 이름 전체 또는 성과 이름을 입력해 주세요.'] : []), ...(row.legalName && (row.legalFamilyName || row.legalGivenName) ? ['법적 이름 전체와 분리한 이름 중 하나만 연결해 주세요.'] : [])],
 templateHref,
 columns: [
  { key: 'legalName', label: '법적 이름 전체', example: '홍길동', validate: maxLengthValidation('법적 이름', 200) },
  ...baseWorkflows[0].columns.map((column) => ({ ...column, required: ['legalFamilyName', 'legalGivenName'].includes(column.key) ? false : column.required, example: ['legalFamilyName', 'legalGivenName'].includes(column.key) ? '' : column.example, label: ({ companyId: '회사', organizationId: '소속 조직', locationId: '근무지', jobProfileId: '직무' } as Record<string, string>)[column.key] ?? column.label })),
  { key: 'workerNumber', label: '사번', required: true, example: 'E001', validate: maxLengthValidation('사번', 100) },
  { key: 'jobTypeId', label: '고용 유형', required: true, example: '정규직' },
  { key: 'effectiveDate', label: '이관 기준일', example: '2026-09-14', validate: dateValidation('이관 기준일') },
  { key: 'reason', label: '이관 사유', example: '기존 인사 명단 이관', validate: maxLengthValidation('이관 사유', 4000) },
 ],
 previewColumns: [...baseWorkflows[0].previewColumns.map((column) => column.label === '구성원' ? { ...column, value: (row: Record<string,string>) => formatPersonDisplayName(row.nicknameKorean, row.legalName || `${row.legalFamilyName ?? ''}${row.legalGivenName ?? ''}`) } : column), { label: '사번', value: (row) => row.workerNumber }, { label: '소속 적용 시작', value: (row) => row.effectiveDate }],
};
export const bulkWorkflows = [...baseWorkflows, { ...employeeImportWorkflow, columns: employeeImportWorkflow.columns.map((column) => column.key === 'effectiveDate' ? { ...column, required: true } : column) }];
